import React, { useEffect } from 'react'
import chroma from 'chroma-js';
import { Box, Flex, Text } from 'rebass';

import Code from './components/Code';
import Button from './components/Button';
import Message from './components/Message';
import Badge from './components/Badge';
import Icon from './components/Icon';
import useLocalStorage from './hooks/useLocalStorage';

import { palettes, ratios, replaceAlias } from './ThemeSwitcher';

export const makeGroupsFromTokens = (colors, useSemanticAlias, selectedPalette) => {

  const groups = [
    {
      label: 'Scales',
      criteria: key => palettes({ alias: false }).some(color => {
        const colorLowercase = color.toLowerCase();
        return key.indexOf(colorLowercase) === 0
      }),
      colors: {},
    },
    {
      label: 'Text',
      criteria: key => (
        key.indexOf('text') === 0
        && key.indexOf('Hover') === -1
        && key.indexOf('Active') === -1
      ),
      colors: {},
    },
    {
      label: 'Icon',
      criteria: key => key.indexOf('icon') === 0,
      colors: {},
    },
    {
      label: 'Background',
      criteria: key => (
        key.indexOf('background') === 0
        && key.indexOf('Hover') === -1
        && key.indexOf('Active') === -1
        && key.indexOf('MutedAlpha') === -1
        && key.indexOf('SubtleAlpha') === -1
      ),
      colors: {},
    },
    {
      label: 'Border',
      criteria: key => key.indexOf('border') === 0,
      colors: {},
    },
    {
      label: 'Other',
      criteria: key => (
        key.indexOf('Hover') === -1
        && key.indexOf('Active') === -1
        && key.indexOf('MutedAlpha') === -1
        && key.indexOf('SubtleAlpha') === -1
      ),
      colors: {},
    }
  ];

  // put colors in groups
  Object.keys(colors).forEach(key => {
    const group = groups.find(g => g.criteria(key));
    if (
      group && 
      key.indexOf(selectedPalette) > -1
      // && key.indexOf('white') === -1
      // && key.indexOf('black') === -1
    ) {
      group.colors[key] = colors[key];
    }
  });
  
  // separate scales
  const scales = palettes({ alias: false }).map(p => p.toLowerCase())
    .filter(p => selectedPalette ? p === selectedPalette : true)
    .reduce((current, scale) => ({
    ...current,
    [scale]: {},
  }), {});
  

  // loop through items in scale group and put in appropriate scale group
  Object.keys(groups.filter(g => g.label === 'Scales')[0].colors).forEach(key => {
    const regexr = /([A-z]*)\d{2,3}/;
    const match = regexr.exec(key);
    if (match) {
      const scale = match[1];
      if (scales[scale]) {
        scales[scale][key] = colors[key];
      }
    }
  });
  groups.filter(g => g.label === 'Scales')[0].colors = scales;

  return groups;
}

const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const Swatch = ({ color, ...props }) => (
  <Box
    css={`
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: ${color};
      border: 1px solid var(--borderNeutralMutedAlpha);
    `}
    {...props}
  />
);

const renderColorValue = (value, useSemanticAlias) => {
  if (value.indexOf('#') === 0) {
    return <Code>{value}</Code>;
  } else if (value.indexOf('$') === 0) {
    return <Code backgroundColor="gray">{'{'}{replaceAlias(value, useSemanticAlias)}{'}'}</Code>;
  }
  const [modifier, color, amount] = value.split(' ');
  if (modifier && color && amount) {
    return (
      <Flex alignItems="center">
        <Icon icon="wrench" fontSize={0} color="var(--icon-gray)" mr={2} />
        <Code mr={2}>{replaceAlias(color, useSemanticAlias)}</Code>
        <Flex alignItems="center">        
          <Icon icon="times" fontSize={0} color="var(--gray400)" mr={2} />
          <Text variant="outline" color="gray">{modifier} ( {amount} )</Text>
        </Flex>
      </Flex>
    );
  }
  return <Code>{value}</Code>;
};

const SwatchTable = ({ children, group }) => (
  <Box
    css={`
      width: 100%;
      overflow-x: auto;

      @media (max-width: 768px) {
        margin-left: -20px;
        padding: 0 20px;
        width: calc(100% + 40px);
      }
    `}
  >
    <Box
      css={`
        border: 1px solid var(--borderNeutralMutedAlpha);
        border-radius: 8px;
        overflow: hidden;
        display: inline-block;

        table {
          margin: -1px;
          white-space: nowrap;
        }
      `}
    >
      <table>
        <thead>
          <tr>
            <th></th>
            <th>
              Token
            </th>
            <th>
              Value
            </th>
            {group.label === 'Scales' && (
              <th>
                Contrast
              </th>
            )}
            {(group.label === 'Text' || group.label === 'Icon') && (
              <th>
                Contrast
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {children}
        </tbody>
      </table>
    </Box>
  </Box>
);

const Tokens = ({ selectedTheme }) => {
  const useSemanticAlias = true;
  const [selectedPalette, setSelectedPalette] = React.useState('');
  const headerRef = React.useRef();
  const [isSticking, setIsSticking] = React.useState(false);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    const handleScroll = () => {
      const headerRefTop = headerRef.current.getBoundingClientRect().top;
      const sticking = headerRefTop <= 0;
      setIsSticking(sticking);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, [headerRef]);

  useEffect(() => {
    Prism.highlightAll();
  }, [selectedPalette]);

  useEffect(() => {
    window.chroma = chroma;
  }, [selectedTheme]);

  if (!selectedTheme) return 'No theme selected';

  const colors = selectedTheme.themes[selectedTheme.theme];

  const groups = makeGroupsFromTokens(colors, useSemanticAlias, selectedPalette);

  const makeSwatchRows = (colors, group) => (
    Object.keys(colors).map((colorKey, idx) => {
      const colorValue = colors[colorKey];
      
      const computedValue = getComputedStyle(document.body).getPropertyValue(`--${colorKey}`).trim();

      let backgroundColorKey = '--background';
      let backgroundColor = getComputedStyle(document.body).getPropertyValue(backgroundColorKey).trim();
      let isOnColor = false;

      if (colorKey.indexOf('OnBackground') > -1) {
        isOnColor = true;
        backgroundColorKey = colorKey.split('On')[1];
      
        // lowercase first letter
        backgroundColorKey = backgroundColorKey.charAt(0).toLowerCase() + backgroundColorKey.slice(1);

        backgroundColor = getComputedStyle(document.body).getPropertyValue(`--${backgroundColorKey}`).trim();
      }

      const contrastAgainstBackground = chroma.contrast(computedValue, backgroundColor);

      const icon = (
        <Icon
          fontSize={1}
          icon="check"
          color={isOnColor ? `var(--${colorKey})` : 'var(--iconGray)'}
          ml={2}
        />
      );
      const textColor = isOnColor ? `var(--${colorKey})` : 'var(--textNeutral)';

      return (
        <tr key={colorKey}>
          <td>
            <Swatch color={`var(--${colorKey})`} />
          </td>
          <td>
            {replaceAlias(colorKey, useSemanticAlias)}
          </td>
          <td>
            {renderColorValue(colorValue, useSemanticAlias)}
          </td>
          {group.label === 'Scales' && (
            <Text as="td" fontSize="0">
              {Object.values(ratios())[idx]}
            </Text>
          )}
          {group.label === 'Text' && (
            <Text as="td" fontSize={1} bg={`var(--${backgroundColorKey})`} color={textColor}>
              {contrastAgainstBackground.toFixed(2)}
              {contrastAgainstBackground > 4.5 && icon}
            </Text>
          )}
          {group.label === 'Icon' && (
            <Text as="td" fontSize={1} bg={`var(--${backgroundColorKey})`} color={textColor}>
              {contrastAgainstBackground.toFixed(2)}
              {contrastAgainstBackground > 3 && icon}
            </Text>
          )}
        </tr>
      );
    })
  );

  return (
    <Box>
      <Flex
        ref={headerRef}
        alignItems="center"
        justifyContent="space-between"
        py={2}
        mb={4}
        css={`
          background: var(--background);
          position: sticky;
          top: -1px;
          z-index: 1;
          border-bottom: 1px solid transparent;
          ${isSticking && `border-bottom-color: var(--borderNeutralMutedAlpha);`}
          transition-duration: .2s;
        `}
      >
        {/* <Box as="label" css="display: block;">
          <Box
            as="input"
            type="checkbox"
            checked={useSemanticAlias}
            onChange={() => setUseSemanticAlias(!useSemanticAlias)}
          />
          <Text fontSize={1} ml={2} css="display: inline;">
            Use semantic alias
          </Text>
        </Box> */}
        <Box
          as="select"
          onChange={e => setSelectedPalette(e.target.value)}
          value={selectedPalette}
        >
          <option value="">All palettes</option>
          {palettes({ alias: useSemanticAlias }).map(colorKey => (
            <option key={colorKey} value={colorKey}>
              {capitalize(replaceAlias(colorKey, useSemanticAlias))}
            </option>
          ))}
        </Box>
      </Flex>
      {/* <h2>Current theme: {selectedTheme.theme}</h2> */}
      <div>
        {groups.map(group => (
          <Box key={group.label} mb={5}>
            <h2>{capitalize(group.label)}</h2> 
              {group.label === 'Scales' ? (
                <Flex
                  flexWrap="wrap"
                  css={`
                    gap: 20px;
                  `}
                >
                  {Object.keys(group.colors).map(scaleKey => (
                    <Box key={scaleKey}>
                      <Text as="h4" mb={3}>
                        {/* {capitalize(replaceAlias(scaleKey, useSemanticAlias))} */}
                        {capitalize(scaleKey)}
                      </Text>
                      <SwatchTable group={group}>
                        {makeSwatchRows(group.colors[scaleKey], group)}
                      </SwatchTable>
                    </Box>
                  ))}
                </Flex>
              ) : (
                <SwatchTable group={group}>
                  {makeSwatchRows(group.colors, group)}
                </SwatchTable>
              )}
          </Box>
        ))}
      </div>
    </Box>
  )
}

export default Tokens